import { defineMessages, useIntl } from "react-intl";
import { MerkPosition } from "@letsbit/malcolmlb";

import { BalanceGroup } from "./useChartBalanceGroups";

export const scope = "hook.useChartBalanceGroups";

export const messages = defineMessages({
  others: {
    id: `${scope}.others`,
    defaultMessage: "otras",
  },
});

export const useChartPositionsGroups = (): {
  computeGroupsByCategory: (positions?: MerkPosition[]) => {
    groups: BalanceGroup[];
    total: number;
  };
  computeGroupsByAsset: (positions?: MerkPosition[]) => {
    groups: BalanceGroup[];
    total: number;
  };
} => {
  const intl = useIntl();

  const computeGroupsByCategory = (positions?: MerkPosition[]) => {
    if (!positions) {
      return { groups: [], total: 0 };
    }

    const totalWithoutNegatives = positions.reduce((acc, position) => {
      return acc + Math.max(+position.market_value, 0);
    }, 0);

    if (totalWithoutNegatives === 0) {
      return { groups: [], total: 0 };
    }

    const positionCategories: Record<string, MerkPosition[]> = positions.reduce(
      (acc, position) => {
        if (position.type in acc) {
          acc[position.type].push(position);
        } else {
          if (position.type === "MON") {
            acc[position.description as string] = [position];
          } else {
            acc[position.type] = [position];
          }
        }
        return acc;
      },
      {} as Record<string, MerkPosition[]>,
    );

    const positionGroups: BalanceGroup[] = Object.entries(
      positionCategories,
    ).map(([key, value]) => {
      const totalCategory = value.reduce(
        (acc, position) => acc + Math.max(+position.market_value, 0),
        0,
      );
      return {
        name: key,
        value: totalCategory,
        percentage: totalCategory / totalWithoutNegatives,
      };
    });

    const others = {
      name: intl.formatMessage(messages.others),
      percentage: 0,
      value: 0,
    };

    positionGroups.forEach((g) => {
      if (totalWithoutNegatives !== 0 && g.percentage < 0.05) {
        others.value += g.value;
        others.percentage += g.percentage;
        g.value = 0;
        g.percentage = 0;
      }
    });

    const sortedGroups = positionGroups
      .sort((a, b) => b.value - a.value)
      .filter((g) => g.value > 0);

    let filteredGroups: BalanceGroup[];

    if (sortedGroups.length > 4) {
      filteredGroups = sortedGroups.slice(0, 3);
      const rest = sortedGroups.slice(3);
      rest.forEach((group) => {
        others.value += group.value;
        others.percentage += group.percentage;
      });
    } else {
      filteredGroups = sortedGroups;
    }

    if (others.value > 0) {
      if (others.percentage < 0.05 && filteredGroups.length > 0) {
        const percentageToSubstract =
          (0.05 - others.percentage) / filteredGroups.length;
        filteredGroups.forEach((g) => {
          g.percentage -= percentageToSubstract;
        });
        others.percentage = 0.05;
      }
      filteredGroups.push(others);
    }
    return { groups: filteredGroups, total: totalWithoutNegatives };
  };

  const computeGroupsByAsset = (positions?: MerkPosition[]) => {
    if (!positions) {
      return { groups: [], total: 0 };
    }

    const totalWithoutNegatives = positions.reduce((acc, position) => {
      return acc + Math.max(+position.market_value, 0);
    }, 0);

    if (totalWithoutNegatives === 0) {
      return { groups: [], total: 0 };
    }

    const positionCategories: Record<string, MerkPosition[]> = positions.reduce(
      (acc, position) => {
        acc[position.asset.name] = [position];
        return acc;
      },
      {} as Record<string, MerkPosition[]>,
    );

    const positionGroups: BalanceGroup[] = Object.entries(
      positionCategories,
    ).map(([key, value]) => {
      const totalCategory = value.reduce(
        (acc, position) => acc + Math.max(+position.market_value, 0),
        0,
      );

      return {
        name: key,
        value: totalCategory,
        percentage: totalCategory / totalWithoutNegatives,
      };
    });

    const others = {
      name: intl.formatMessage(messages.others),
      percentage: 0,
      value: 0,
    };

    positionGroups.forEach((g) => {
      if (totalWithoutNegatives !== 0 && g.percentage < 0.05) {
        others.value += g.value;
        others.percentage += g.percentage;
        g.value = 0;
        g.percentage = 0;
      }
    });

    const sortedGroups = positionGroups
      .sort((a, b) => b.value - a.value)
      .filter((g) => g.value > 0);

    let filteredGroups: BalanceGroup[];

    if (sortedGroups.length > 4) {
      filteredGroups = sortedGroups.slice(0, 3);
      const rest = sortedGroups.slice(3);
      rest.forEach((group) => {
        others.value += group.value;
        others.percentage += group.percentage;
      });
    } else {
      filteredGroups = sortedGroups;
    }

    if (others.value > 0) {
      if (others.percentage < 0.05 && filteredGroups.length > 0) {
        const percentageToSubstract =
          (0.05 - others.percentage) / filteredGroups.length;
        filteredGroups.forEach((g) => {
          g.percentage -= percentageToSubstract;
        });
        others.percentage = 0.05;
      }
      filteredGroups.push(others);
    }
    return { groups: filteredGroups, total: totalWithoutNegatives };
  };

  return { computeGroupsByCategory, computeGroupsByAsset };
};
